import React, { useCallback } from 'react';

import { Typography } from '@mui/material';
import { ToggleSwitch } from 'src/components/generic/ToggleSwitch';
import type { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

interface ExternalToolSwitchProps {
  readonly allowed: boolean;
  readonly busy: boolean;
  readonly onToggle: (toolSlugs: readonly string[], enabled: boolean) => void;
  readonly tool: Apollo.ExternalToolkitSimpleFragment['tools'][number];
}

const useStyles = makeStyles()(theme => ({
  control: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  copy: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  description: {
    textAlign: 'justify',
  },
  name: {
    fontWeight: 600,
  },
  /* No hover state: the switch is the only target, so a row-wide tint would lie. */
  root: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
  },
}));

export const ExternalToolSwitch = React.memo(function ExternalToolSwitch({
  allowed,
  busy,
  onToggle,
  tool,
}: ExternalToolSwitchProps): JSX.Element {
  const { classes } = useStyles();
  const { checked, disabled, handleChange, handleKeyDown } = useExternalToolToggle({
    allowed,
    busy,
    onToggle,
    tool,
  });
  return (
    <div className={classes.root}>
      <div className={classes.copy}>
        <Typography className={classes.name} variant="body2">
          {tool.name}
        </Typography>
        {Boolean(tool.description) && (
          <Typography className={classes.description} color="text.secondary" variant="caption">
            {tool.description}
          </Typography>
        )}
      </div>
      <div className={classes.control}>
        <ToggleSwitch
          aria-checked={checked}
          aria-disabled={disabled}
          aria-label={`Enable ${tool.name}`}
          disabled={disabled}
          role="switch"
          tabIndex={disabled ? -1 : 0}
          toggle={handleChange}
          toggled={checked}
          onKeyDown={handleKeyDown}
        />
      </div>
    </div>
  );
});

function useExternalToolToggle({ allowed, busy, onToggle, tool }: ExternalToolSwitchProps) {
  const checked = tool.enabled;
  const disabled = busy || (!allowed && !checked);
  const handleChange = useCallback(
    () => onToggle([tool.slug], !checked),
    [checked, onToggle, tool.slug]
  );
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      if (!disabled) handleChange();
    },
    [disabled, handleChange]
  );
  return { checked, disabled, handleChange, handleKeyDown };
}
